/**
 * Single source of truth for invoice display status.
 *
 * `invoices.status` (draft / sent / partial / paid) is payment-driven and set
 * by the backend (create as draft, Send sets 'sent', record_invoice_payment
 * RPC sets partial/paid). 'past_due' is derived here for display only — it
 * is a sent invoice whose due date has passed with a balance still owed.
 */
export type InvoiceDisplayStatus = 'draft' | 'sent' | 'partial' | 'paid' | 'past_due'

export interface InvoiceStatusInput {
  status?: string | null
  balance_due?: number | null
  due_date?: string | null
}

export function getInvoiceDisplayStatus(invoice: InvoiceStatusInput): InvoiceDisplayStatus {
  const status = invoice.status || 'draft'
  if (status === 'paid' || status === 'partial' || status === 'draft') {
    return status
  }
  const balanceDue = Number(invoice.balance_due ?? 0)
  if (status === 'sent' && balanceDue > 0 && invoice.due_date && new Date(invoice.due_date) < new Date()) {
    return 'past_due'
  }
  return 'sent'
}

export function getInvoiceStatusLabel(displayStatus: InvoiceDisplayStatus | string): string {
  switch (displayStatus) {
    case 'past_due':
      return 'Past due'
    case 'sent':
      return 'Sent'
    default:
      return displayStatus.charAt(0).toUpperCase() + displayStatus.slice(1)
  }
}

export function getInvoiceStatusColor(displayStatus: InvoiceDisplayStatus | string): string {
  switch (displayStatus) {
    case 'paid':
      return 'bg-green-100 text-green-800'
    case 'partial':
      return 'bg-blue-100 text-blue-800'
    case 'sent':
      return 'bg-yellow-100 text-yellow-800'
    case 'past_due':
      return 'bg-red-100 text-red-800'
    case 'draft':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}
